/* eslint-disable @typescript-eslint/no-shadow */
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Autocomplete, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FocusEvent, useEffect, useState } from 'react';
import { ListUserDto } from '@codefluegel/zeta-change-typescript-client';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useUserApiFactory } from '../../ApiClient';
import DialogPopover from '../DialogPopover/DialogPopover';
import {
  DialogDateLabel,
  DialogText,
  DialogTitle,
} from '../../constants/changes';
import { DialogType, StatusEventTypes } from '../../types/changes';
import { useChangeStore } from '../../store/changeStore';
import { useUserStore } from '../../store/userStore';
import LocalizationWrapper from '../Date/LocalizationWrapper';
import { formatLocalDate } from '../../utils/utils';

type ConfirmChangeDialogProps = {
  isOpen: boolean;
  modalType: DialogType;
  isLoading: boolean;
  handleClose: () => void;
  handleSubmit: (comment: string) => void;
};

const ConfirmChangeDialog = observer(
  ({
    isOpen,
    modalType,
    isLoading,
    handleClose,
    handleSubmit,
  }: ConfirmChangeDialogProps) => {
    const [dateValue, setDateValue] = useState<string | Date | null>(null);
    const [autoselectValue, setAutoselectValue] = useState<string | null>(null);
    const { users } = useUserStore();
    const [manager, setChangeManager] = useState<ListUserDto>();
    const [isDesktopDatePickerOpen, setIsDesktopDatePickerOpen] =
      useState(false);
    const isEvalAcceptedStatus = modalType === StatusEventTypes.evalAccepted;
    const isNewStatus = modalType === StatusEventTypes.accept;
    const isCrWorkpackageInProgressStatus =
      modalType === StatusEventTypes.crWorkpackageInProgress ||
      modalType === StatusEventTypes.CrWorkpackagesInInternalProgress;

    const userApi = useUserApiFactory();

    const isReject =
      modalType === StatusEventTypes.reject ||
      modalType === StatusEventTypes.internalApprovalRejected ||
      modalType === StatusEventTypes.rejectAll;

    const { t } = useTranslation();
    const titles = DialogTitle();
    const texts = DialogText();
    const label = DialogDateLabel();

    const { change } = useChangeStore();

    useEffect(() => {
      (async () => {
        if (isEvalAcceptedStatus && !autoselectValue) {
          const changeManager = users?.find(
            user =>
              user.id.toLocaleLowerCase() ===
              change?.changeManager?.toLowerCase(),
          );
          if (changeManager) {
            setChangeManager(changeManager);
            setAutoselectValue(changeManager.id);
          }
        }
      })();
    }, [
      change?.changeManager,
      isEvalAcceptedStatus,
      userApi,
      users,
      autoselectValue,
    ]);

    const onChangeDateHandler = (newVal: string | Date | null) => {
      setDateValue(newVal);
    };

    const onChangeHandler = (newVal: string | null) => {
      setAutoselectValue(newVal);
    };

    const onSubmitHandler = () => {
      if (isEvalAcceptedStatus && autoselectValue)
        handleSubmit(autoselectValue);
      else if (
        !isEvalAcceptedStatus &&
        !isCrWorkpackageInProgressStatus &&
        !isNewStatus
      ) {
        handleSubmit(String(dateValue) || '');
      } else if (isReject) {
        handleSubmit(String(dateValue) || '');
      } else if (dateValue) {
        const date = formatLocalDate(dateValue);
        handleSubmit(date);
      }
    };

    const onDesktopDatePickerFocus = (
      e: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>,
    ) => {
      setIsDesktopDatePickerOpen(true);
      e.target.blur();
    };

    return (
      <DialogPopover
        isOpen={isOpen}
        isDisabledConfirm={isEvalAcceptedStatus ? !autoselectValue : !dateValue}
        title={titles[modalType]}
        contentText={texts[modalType]}
        handleClose={handleClose}
        handleSubmit={onSubmitHandler}
        isLoading={isLoading}
      >
        {!isEvalAcceptedStatus &&
          !isCrWorkpackageInProgressStatus &&
          !isNewStatus && (
            <TextField
              css={css`
                .MuiInput-underline:before {
                  border-bottom: 1px solid #ececec;
                }

                .MuiInputLabel-asterisk {
                  color: #ff0000;
                }
              `}
              required
              multiline
              autoFocus
              margin="dense"
              id="name"
              label={
                isReject
                  ? t('dialog.ReasonToReject')
                  : t('dialog.ReasonToChange')
              }
              type="text"
              fullWidth
              variant="standard"
              onChange={e => onChangeDateHandler(e.target.value)}
            />
          )}

        {isEvalAcceptedStatus && manager && (
          <Autocomplete
            value={manager}
            popupIcon={
              <ExpandMoreIcon
                color="primary"
                css={css`
                  color: #80acd4;
                `}
              />
            }
            css={css`
              margin-top: 8px;

              icon: {
                fill: 'red';
              }

              .MuiInput-underline:before {
                border-bottom: 1px solid #ececec;
              }
              .MuiInputLabel-asterisk {
                color: #ff0000;
              }
            `}
            options={users ?? []}
            renderOption={({ ...props }, option) => (
              <li {...props} key={option.id}>
                {option.displayName}
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(e, value: ListUserDto | null) => {
              if (value) {
                setChangeManager(value);
                onChangeHandler(value ? value.id : null);
              }
            }}
            getOptionLabel={option => option.displayName}
            defaultValue={manager}
            renderInput={(params: any) => (
              <TextField
                {...params}
                error={!autoselectValue}
                required
                label={t('calc.calcResp')}
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  style: {
                    paddingLeft: '16px',
                    paddingBottom: '6px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: '300',
                  },
                }}
                InputLabelProps={{
                  style: {
                    paddingLeft: '20px',
                    fontWeight: 600,
                    color: '#333333',
                  },
                }}
              />
            )}
          />
        )}

        {(isCrWorkpackageInProgressStatus || isNewStatus) && (
          <LocalizationWrapper>
            <DesktopDatePicker
              open={isDesktopDatePickerOpen}
              onOpen={() => setIsDesktopDatePickerOpen(true)}
              onClose={() => setIsDesktopDatePickerOpen(false)}
              format="dd.MM.yyyy"
              label={label[modalType]}
              value={new Date(dateValue || '')}
              onChange={onChangeDateHandler}
              slotProps={{
                textField: {
                  style: { width: '100%', marginTop: 16 },
                  variant: 'standard',
                  error: !dateValue,
                  autoComplete: 'off',
                  InputProps: {
                    onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                      onDesktopDatePickerFocus(e),
                    sx: {
                      marginTop: 3,
                      paddingLeft: 1,
                      '& .MuiSvgIcon-root': {
                        color: '#80acd4',
                      },
                    },
                  },
                  InputLabelProps: { sx: { paddingLeft: 1 } },
                },
                inputAdornment: { sx: { paddingRight: 1 } },
              }}
            />
          </LocalizationWrapper>
        )}
      </DialogPopover>
    );
  },
);

export default ConfirmChangeDialog;
